import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Target, ChevronRight, Loader2, Mail, Phone, Hash, User, BookOpen, Calendar, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { Sec, FormField, SelectField, TextareaField } from '../components/Shared';
import { MEMBERSHIP_SCRIPT_URL, FAQS } from '../data/websiteData';

const MembershipPage = ({ showToast }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', phone: '', roll: '', department: '', year: '', track: '', reason: '' });
  const [agreedPolicy, setAgreedPolicy] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [faqs, setFaqs] = useState(FAQS.map(f => ({ ...f, open: f.open || false })));

  const handleChange = useCallback((id, val) => {
    setForm(p => ({ ...p, [id]: val }));
    setErrors(p => ({ ...p, [id]: '' }));
  }, []);

  const validate1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid institutional email required';
    if (!form.phone.trim() || form.phone.length < 11) e.phone = 'Valid 11-digit phone number required';
    if (!form.roll.trim()) e.roll = 'Student roll number is required';
    return e;
  };

  const validate2 = () => {
    const e = {};
    if (!form.department.trim()) e.department = 'Department is required';
    if (!form.year) e.year = 'Academic year is required';
    if (!form.track) e.track = 'Please select a technical track';
    return e;
  };

  const next = () => {
    const errs = step === 1 ? validate1() : validate2();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(s => s + 1);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const submit = async e => {
    e.preventDefault();
    if (!agreedPolicy) { setPolicyError('Agreement is required to initialize sync.'); return; }
    setPolicyError('');
    setStatus('loading');
    try {
      const res = await fetch(MEMBERSHIP_SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          source: 'Membership Form',
          timestamp: new Date().toISOString(),
          agreedPolicy: agreedPolicy ? 'Yes' : 'No',
          Timestamp: new Date().toISOString(),
          Name: form.name,
          Email: form.email,
          Phone: Number(form.phone) || form.phone,
          Phone_Number: Number(form.phone) || form.phone,
          PhoneNumber: Number(form.phone) || form.phone,
          phone_number: Number(form.phone) || form.phone,
          "Phone Number": Number(form.phone) || form.phone,
          "phone number": Number(form.phone) || form.phone,
          Roll: Number(form.roll) || form.roll,
          Roll_Number: Number(form.roll) || form.roll,
          RollNumber: Number(form.roll) || form.roll,
          roll_number: Number(form.roll) || form.roll,
          "Roll Number": Number(form.roll) || form.roll,
          "roll number": Number(form.roll) || form.roll,
          Department: form.department,
          Year: form.year,
          Track: form.track,
          Reason: form.reason,
          AgreedPolicy: agreedPolicy ? 'Yes' : 'No'
        })
      });
      
      if (!res.ok) throw new Error('API Error');
      const data = await res.json();
      if (data.status === 'error') throw new Error(data.message || 'API Error');

      setStatus('success');
      showToast("Application transmitted to the Technical Board.", 'success');
    } catch {
      showToast('Transmission failed. Check network protocols.', 'error');
      setStatus('idle');
    }
  };

  return (
    <div className="af">
      <Sec py={160} bg="var(--grad-hero)">
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 64, alignItems: 'start' }} className="membership-grid">
          <div>
            <div className="eyebrow">Neural Enrollment</div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 800, color: 'var(--on-surface)', lineHeight: 1.1, letterSpacing: 'var(--ls-tight)', marginBottom: 24 }}>
              Join the <span className="gtext">Digital</span> Collective.
            </h1>
            <p style={{ fontSize: 18, color: 'var(--on-surface-variant)', lineHeight: 1.6, marginBottom: 48 }}>
              Initialize your connection to Dhaka's most advanced student IT cell. Access the lab, repositories, and our peer-to-peer technical network.
            </p>

            <div className="cs" style={{ padding: 32, animationDelay: '0.2s' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 24 }}>Enrollment FAQ</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {faqs.map((faq, i) => (
                  <div key={i} style={{ background: faq.open ? 'var(--surface)' : 'transparent', borderRadius: 4, transition: 'all .3s' }}>
                    <button 
                      onClick={() => setFaqs(f => f.map((x, idx) => idx === i ? { ...x, open: !x.open } : x))}
                      style={{ width: '100%', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 600, color: faq.open ? 'var(--primary)' : 'var(--on-surface)' }}>{faq.q}</span>
                      <ChevronDown size={16} style={{ transform: faq.open ? 'rotate(180deg)' : 'none', transition: 'all .3s', color: 'var(--slate-400)' }} />
                    </button>
                    {faq.open && <div style={{ padding: '0 16px 16px', fontSize: 14, color: 'var(--on-surface-variant)', lineHeight: 1.6 }} className="af">{faq.a}</div>}
                  </div>
                ))}
              </div>
            </div>
            
            {/* NEW BENEFITS SECTION */}
            <div style={{ marginTop: 48 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 24 }}>Membership Benefits</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { title: 'Exclusive Workshops', desc: 'Gain access to members-only hands-on coding and design sessions.' },
                  { title: 'Mentorship', desc: 'Learn directly from seniors and track leads in a peer-to-peer environment.' },
                  { title: 'Official Certifications', desc: 'Earn verified certificates for completing technical tracks and hackathons.' },
                  { title: 'Portfolio Building', desc: 'Work on real projects that you can showcase on your resume.' }
                ].map((b, i) => (
                  <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                      <Check size={14} />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 4 }}>{b.title}</div>
                      <div style={{ fontSize: 14, color: 'var(--on-surface-variant)', lineHeight: 1.5 }}>{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card form-card">
            {status === 'success' ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }} className="asc">
                <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--surface-container-low)', color: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <Check size={32} />
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 12 }}>Sync Initialized</h3>
                <p style={{ color: 'var(--on-surface-variant)', lineHeight: 1.6, marginBottom: 32 }}>Your credentials have been uploaded. The Technical Committee will review your uplink within 3-5 operational days.</p>
                <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => { setStep(1); setStatus('idle'); }}>Apply for Another Account</button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32 }}>
                  {[1, 2, 3].map(n => (
                    <React.Fragment key={n}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: step >= n ? 'var(--primary)' : 'var(--surface-container-low)', color: step >= n ? '#fff' : 'var(--slate-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, transition: 'all .4s' }}>
                        {step > n ? <Check size={16} /> : n}
                      </div>
                      {n < 3 && <div style={{ flex: 1, height: 2, background: step > n ? 'var(--primary)' : 'var(--surface-container-low)', margin: '0 8px', transition: 'all .4s' }} />}
                    </React.Fragment>
                  ))}
                </div>

                <form onSubmit={submit} noValidate>
                  {step === 1 && (
                    <div className="au">
                      <FormField id="name" label="Full Name" placeholder="Student Name" value={form.name} onChange={handleChange} error={errors.name} icon={<User size={14} />} />
                      <FormField id="email" label="Institutional Email" placeholder="name@college.edu" type="email" value={form.email} onChange={handleChange} error={errors.email} icon={<Mail size={14} />} />
                      <div className="g2">
                        <FormField id="phone" label="Contact Number" placeholder="01XXX XXXXXX" type="number" value={form.phone} onChange={handleChange} error={errors.phone} icon={<Phone size={14} />} />
                        <FormField id="roll" label="College Roll" placeholder="7-digit ID" type="number" value={form.roll} onChange={handleChange} error={errors.roll} icon={<Hash size={14} />} />
                      </div>
                      <button className="btn btn-primary btn-lg" type="button" onClick={next} style={{ width: '100%', marginTop: 20 }}>Proceed to Academics <ChevronRight size={16} /></button>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="au">
                      <FormField id="department" label="Department / Division" placeholder="e.g. Science" value={form.department} onChange={handleChange} error={errors.department} icon={<BookOpen size={14} />} />
                      <SelectField id="year" label="Academic Level" value={form.year} onChange={handleChange} error={errors.year} icon={<Calendar size={14} />} options={['HSC 1st Year', 'HSC 2nd Year', 'Alumni']} />
                      <SelectField id="track" label="Preferred Track" value={form.track} onChange={handleChange} error={errors.track} icon={<Target size={14} />} options={['Web Dev', 'UI/UX Design', 'Networking', 'Cybersecurity', 'Graphics & Media']} />
                      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                        <button className="btn btn-ghost" type="button" onClick={() => setStep(1)} style={{ flex: 1 }}>Back</button>
                        <button className="btn btn-primary btn-lg" type="button" onClick={next} style={{ flex: 2 }}>Check Details <ChevronRight size={16} /></button>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="au">
                      <TextareaField id="reason" label="Technical Intent (Optional)" placeholder="Why do you wish to join the cell?" value={form.reason} onChange={handleChange} />
                      <div style={{ padding: 16, background: 'var(--surface-container-low)', borderRadius: 4, marginBottom: 24 }}>
                        <label style={{ display: 'flex', gap: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={agreedPolicy} onChange={e => setAgreedPolicy(e.target.checked)} style={{ marginTop: 4, width: 16, height: 16 }} />
                          <span style={{ fontSize: 13, color: 'var(--on-surface-variant)', lineHeight: 1.5 }}>
                            I agree to the <button type="button" onClick={() => navigate('/membership-policy')} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, padding: 0, textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit' }}>Internal Protocol</button> and membership conditions.
                          </span>
                        </label>
                        {policyError && <p style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 8, fontWeight: 600 }}><AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {policyError}</p>}
                      </div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button className="btn btn-ghost" type="button" onClick={() => setStep(2)} style={{ flex: 1 }}>Back</button>
                        <button className="btn btn-primary btn-lg" type="submit" disabled={status === 'loading'} style={{ flex: 2 }}>
                          {status === 'loading' ? <><Loader2 size={18} className="spin" /> Syncing…</> : <><Rocket size={18} /> Initialize Enrollment</>}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </Sec>
    </div>
  );
};

export default MembershipPage;
